import { useState, useMemo } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useParams, Link } from "react-router-dom";
import { FaPlus, FaCog, FaArrowLeft, FaGripVertical, FaFilter, FaTimes } from "react-icons/fa";

import Button from "../components/Button";
import Modal from "../components/Modal";
import FormInput from "../components/FormInput";
import { useToast } from "../components/Toast";
import SalesFunnelItemCard from "../components/SalesFunnelItemCard";
import SalesFunnelItemDetailModal from "../components/SalesFunnelItemDetailModal";
import { HeroIconByName } from "../components/HeroIconByName";
import useAuthConsumer from "../hooks/useAuth";

type FilterOperator = "eq" | "neq" | "gt" | "lt";

interface FieldFilter {
  fieldDefinitionId: string;
  operator: FilterOperator;
  value: string;
}

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: "=",
  neq: "≠",
  gt: ">",
  lt: "<",
};

function getFieldValueRaw(item: any, fieldDefinitionId: string): any {
  const fv = item.fieldValues?.find((f: any) => f.fieldDefinition.id === fieldDefinitionId);
  return fv?.value;
}

function toComparableValue(value: any, type: string): number | string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "object" && value?.error) return null;
  if (type === "NUMBER" || type === "COMPUTED") {
    const num = typeof value === "number" ? value : parseFloat(value);
    return isNaN(num) ? null : num;
  }
  if (type === "DATE") {
    const d = new Date(value).getTime();
    return isNaN(d) ? null : d;
  }
  if (type === "BOOLEAN") {
    return value === true || value === "true" ? "true" : "false";
  }
  if (type === "SELECT") {
    return value?.label ?? String(value);
  }
  if (type === "MULTI_SELECT") {
    if (Array.isArray(value)) return value.map((v) => v?.label ?? v).join(", ");
    return String(value);
  }
  return String(value);
}

const PSEUDO_FIELDS = [
  { id: "__assignedUser", label: "Assigned User", type: "USER" },
  { id: "__author", label: "Author", type: "USER" },
];

function isPseudoField(id: string): boolean {
  return id.startsWith("__");
}

function matchesPseudoFilter(item: any, filter: FieldFilter): boolean {
  if (filter.value === "") return true;
  if (filter.fieldDefinitionId === "__assignedUser") {
    const assignedIds = item.assignedUsers?.map((au: any) => au.user.id) || [];
    const hasUser = assignedIds.includes(filter.value);
    return filter.operator === "eq" ? hasUser : !hasUser;
  }
  if (filter.fieldDefinitionId === "__author") {
    const isAuthor = item.author?.id === filter.value;
    return filter.operator === "eq" ? isAuthor : !isAuthor;
  }
  return true;
}

function matchesFilter(item: any, filter: FieldFilter, fieldType: string): boolean {
  if (isPseudoField(filter.fieldDefinitionId)) {
    return matchesPseudoFilter(item, filter);
  }
  const rawValue = getFieldValueRaw(item, filter.fieldDefinitionId);
  const itemValue = toComparableValue(rawValue, fieldType);
  const filterValue = toComparableValue(filter.value, fieldType);

  if (filterValue === null) return true;

  if (itemValue === null) {
    return filter.operator === "neq";
  }

  if (filter.operator === "eq") {
    if (fieldType === "SELECT" || fieldType === "MULTI_SELECT") {
      return String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
    }
    return itemValue === filterValue;
  }
  if (filter.operator === "neq") {
    if (fieldType === "SELECT" || fieldType === "MULTI_SELECT") {
      return !String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
    }
    return itemValue !== filterValue;
  }
  if (filter.operator === "gt") {
    if (typeof itemValue === "number" && typeof filterValue === "number") {
      return itemValue > filterValue;
    }
    return String(itemValue) > String(filterValue);
  }
  if (filter.operator === "lt") {
    if (typeof itemValue === "number" && typeof filterValue === "number") {
      return itemValue < filterValue;
    }
    return String(itemValue) < String(filterValue);
  }
  return true;
}

function getOperatorsForType(type: string): FilterOperator[] {
  if (type === "NUMBER" || type === "DATE" || type === "COMPUTED") {
    return ["eq", "neq", "gt", "lt"];
  }
  if (type === "USER") {
    return ["eq", "neq"];
  }
  return ["eq", "neq"];
}

function getInputTypeForFieldType(type: string): string {
  if (type === "NUMBER" || type === "COMPUTED") return "number";
  if (type === "DATE") return "date";
  if (type === "BOOLEAN") return "checkbox";
  return "text";
}

function formatComputedValue(value: any, currency?: string): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    const formatted = value.toLocaleString();
    return currency ? `${currency}\u00a0${formatted}` : formatted;
  }
  return String(value);
}

const GET_BOARD = gql`
  query GetSalesFunnelBoard($id: ID!) {
    salesFunnelBoard(id: $id) {
      id
      name
      description
      stages {
        id
        name
        color
        position
        computedValues {
          id
          label
          value
          error
          icon
          currency
        }
        items {
          id
          title
          position
          archived
          author {
            id
            name
            picture
          }
          fieldValues {
            id
            value
            fieldDefinition {
              id
              label
              type
              icon
              options
            }
          }
          assignedUsers {
            id
            user {
              id
              name
              picture
            }
            role
          }
        }
      }
      fieldDefinitions {
        id
        label
        type
        icon
        defaultValue
        options
        required
        position
      }
      computedValues {
        id
        label
        value
        error
        icon
        currency
      }
      cardLayout
    }
  }
`;

const CREATE_STAGE = gql`
  mutation CreateSalesFunnelStage($boardId: ID!, $name: String!, $color: String) {
    createSalesFunnelStage(boardId: $boardId, name: $name, color: $color) {
      success
      message
      id
    }
  }
`;

const CREATE_ITEM = gql`
  mutation CreateSalesFunnelItem($boardId: ID!, $stageId: ID!, $title: String!) {
    createSalesFunnelItem(boardId: $boardId, stageId: $stageId, title: $title) {
      success
      message
      id
    }
  }
`;

const MOVE_ITEM = gql`
  mutation MoveSalesFunnelItem($id: ID!, $stageId: ID!, $position: Int!) {
    moveSalesFunnelItem(id: $id, stageId: $stageId, position: $position) {
      success
      message
      automationsTriggered
      automationErrors
    }
  }
`;

const ADD_ITEM_USER = gql`
  mutation AddSalesFunnelItemUser($itemId: ID!, $userId: ID!, $role: String) {
    addSalesFunnelItemUser(itemId: $itemId, userId: $userId, role: $role) {
      success
      message
    }
  }
`;

export default function SalesFunnelBoardPage() {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuthConsumer();
  const { data, loading, refetch } = useQuery(GET_BOARD, {
    variables: { id },
    skip: !id,
  });

  const { showAutomationToast } = useToast();
  const [createStage] = useMutation(CREATE_STAGE);
  const [createItem] = useMutation(CREATE_ITEM);
  const [moveItem] = useMutation(MOVE_ITEM);
  const [addItemUser] = useMutation(ADD_ITEM_USER);

  const [showCreateStageModal, setShowCreateStageModal] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [newStageColor, setNewStageColor] = useState("#3B82F6");

  const [creatingItemInStage, setCreatingItemInStage] = useState<string | null>(null);
  const [newItemTitle, setNewItemTitle] = useState("");

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const [draggedItem, setDraggedItem] = useState<any>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FieldFilter[]>([]);

  const board = data?.salesFunnelBoard;

  const boardUsers = useMemo(() => {
    const userMap: Record<string, { id: string; name: string }> = {};
    board?.stages?.forEach((stage: any) => {
      stage.items?.forEach((item: any) => {
        if (item.author) userMap[item.author.id] = item.author;
        item.assignedUsers?.forEach((au: any) => {
          if (au.user) userMap[au.user.id] = au.user;
        });
      });
    });
    return Object.values(userMap);
  }, [board]);

  const fieldDefMap = useMemo(() => {
    const map: Record<string, any> = {};
    board?.fieldDefinitions?.forEach((fd: any) => { map[fd.id] = fd; });
    return map;
  }, [board]);

  const filteredStages = useMemo(() => {
    if (!board?.stages) return [];
    if (filters.length === 0) return board.stages;
    return board.stages.map((stage: any) => ({
      ...stage,
      originalItemsCount: stage.items?.length || 0,
      items: stage.items?.filter((item: any) =>
        filters.every((f) => {
          if (isPseudoField(f.fieldDefinitionId)) {
            return matchesPseudoFilter(item, f);
          }
          const fd = fieldDefMap[f.fieldDefinitionId];
          if (!fd) return true;
          return matchesFilter(item, f, fd.type);
        })
      ),
    }));
  }, [board, filters, fieldDefMap]);

  function addFilter() {
    const firstField = board?.fieldDefinitions?.[0];
    const firstId = firstField?.id || PSEUDO_FIELDS[0].id;
    setFilters([...filters, { fieldDefinitionId: firstId, operator: "eq", value: "" }]);
  }

  function updateFilter(index: number, patch: Partial<FieldFilter>) {
    setFilters(filters.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }

  function removeFilter(index: number) {
    setFilters(filters.filter((_, i) => i !== index));
  }

  const handleCreateStage = async () => {
    if (!newStageName.trim() || !id) return;

    try {
      await createStage({
        variables: {
          boardId: id,
          name: newStageName,
          color: newStageColor,
        },
      });
      setShowCreateStageModal(false);
      setNewStageName("");
      setNewStageColor("#3B82F6");
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleCreateItem = async (stageId: string) => {
    if (!newItemTitle.trim() || !id) return;

    try {
      const result = await createItem({
        variables: {
          boardId: id,
          stageId,
          title: newItemTitle,
        },
      });
      const newItemId = result.data?.createSalesFunnelItem?.id;
      if (newItemId && currentUser?.id) {
        await addItemUser({
          variables: { itemId: newItemId, userId: currentUser.id, role: "assignee" },
        });
      }
      setCreatingItemInStage(null);
      setNewItemTitle("");
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleQuickCreateItem = async (stageId: string) => {
    if (!id) return;

    try {
      const result = await createItem({
        variables: {
          boardId: id,
          stageId,
          title: "New Item",
        },
      });
      const newItemId = result.data?.createSalesFunnelItem?.id;
      if (newItemId && currentUser?.id) {
        await addItemUser({
          variables: { itemId: newItemId, userId: currentUser.id, role: "assignee" },
        });
      }
      if (newItemId) {
        setSelectedItemId(newItemId);
      }
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleDragStart = (e: React.DragEvent, item: any, stageId: string) => {
    setDraggedItem({ ...item, sourceStageId: stageId });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e: React.DragEvent, targetStageId: string, targetPosition: number) => {
    e.preventDefault();
    if (!draggedItem) return;

    try {
      const result = await moveItem({
        variables: {
          id: draggedItem.id,
          stageId: targetStageId,
          position: targetPosition,
        },
      });
      
      // Show automation toast if any automations were triggered
      const { automationsTriggered, automationErrors } = result.data?.moveSalesFunnelItem || {};
      if (automationsTriggered > 0 || automationErrors?.length > 0) {
        showAutomationToast(automationsTriggered || 0, automationErrors || []);
      }
      
      refetch();
    } catch (err) {
      alert((err as Error).message);
    }

    setDraggedItem(null);
  };

  if (loading) {
    return (
      <div className="w-full p-4 pt-6">
        <div className="text-center py-10">Loading board...</div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="w-full p-4 pt-6">
        <div className="text-center py-10 text-gray-500">
          Board not found or you don't have access.
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-4 pt-6 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <Link to="/funnel" className="text-gray-400 hover:text-gray-600">
            <FaArrowLeft />
          </Link>
          <div>
            <h1 className="text-2xl text-title font-bold">{board.name}</h1>
            {board.description && (
              <p className="text-sm text-gray-500">{board.description}</p>
            )}

          </div>
        </div>
        <div className="flex space-x-2">


          {board.computedValues && board.computedValues.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-1">
              {board.computedValues.map((cv: any) => (
                <span key={cv.id} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                  <HeroIconByName name={cv.icon} className="w-3 h-3 flex-shrink-0" />
                  <span className="text-gray-500">{cv.label}:</span>
                  <span>{cv.error ? "⚠" : formatComputedValue(cv.value, cv.currency)}</span>
                </span>
              ))}
            </div>
          )}

          <Button
            variant={filters.length > 0 ? "primary" : "outlined"}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter className="inline mr-2" />
            Filter{filters.length > 0 ? ` (${filters.length})` : ""}
          </Button>
          <Button variant="outlined" onClick={() => setShowCreateStageModal(true)}>
            <FaPlus className="inline mr-2" />
            Add Stage
          </Button>
          <Link to={`/funnel/${id}/settings`}>
            <Button variant="outlined">
              <FaCog className="inline mr-2" />
              Settings
            </Button>
          </Link>
        </div>
      </div>

      {showFilters && (
        <div className="flex-shrink-0 mb-4 bg-white rounded-lg shadow-sm p-3">
          <div className="flex flex-wrap items-center gap-2">
            {filters.map((filter, index) => {
              const isPseudo = isPseudoField(filter.fieldDefinitionId);
              const pseudoField = PSEUDO_FIELDS.find((p) => p.id === filter.fieldDefinitionId);
              const fd = fieldDefMap[filter.fieldDefinitionId];
              const fieldType = isPseudo ? pseudoField?.type : fd?.type;
              const operators = fieldType ? getOperatorsForType(fieldType) : ["eq", "neq"] as FilterOperator[];
              const inputType = fd ? getInputTypeForFieldType(fd.type) : "text";
              const selectOptions = fd?.options?.options || fd?.options;

              return (
                <div key={index} className="flex items-center gap-1 bg-gray-50 rounded-md px-2 py-1">
                  <select
                    className="text-sm border-none bg-transparent focus:ring-0"
                    value={filter.fieldDefinitionId}
                    onChange={(e) => {
                      const newPseudo = PSEUDO_FIELDS.find((p) => p.id === e.target.value);
                      const newFd = board.fieldDefinitions.find((f: any) => f.id === e.target.value);
                      const newType = newPseudo?.type || newFd?.type;
                      const newOperators = newType ? getOperatorsForType(newType) : ["eq", "neq"] as FilterOperator[];
                      updateFilter(index, {
                        fieldDefinitionId: e.target.value,
                        operator: newOperators[0],
                        value: "",
                      });
                    }}
                  >
                    {board.fieldDefinitions?.map((fd: any) => (
                      <option key={fd.id} value={fd.id}>{fd.label}</option>
                    ))}
                    {PSEUDO_FIELDS.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                  <select
                    className="text-sm border-none bg-transparent focus:ring-0"
                    value={filter.operator}
                    onChange={(e) => updateFilter(index, { operator: e.target.value as FilterOperator })}
                  >
                    {operators.map((op) => (
                      <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
                    ))}
                  </select>
                  {isPseudo ? (
                    <select
                      className="text-sm border rounded px-1 py-0.5"
                      value={filter.value}
                      onChange={(e) => updateFilter(index, { value: e.target.value })}
                    >
                      <option value="">Select user...</option>
                      {boardUsers.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  ) : fd?.type === "SELECT" && Array.isArray(selectOptions) ? (
                    <select
                      className="text-sm border rounded px-1 py-0.5"
                      value={filter.value}
                      onChange={(e) => updateFilter(index, { value: e.target.value })}
                    >
                      <option value="">Select...</option>
                      {selectOptions.map((opt: any) => (
                        <option key={opt.value ?? opt.label} value={opt.value ?? opt.label}>
                          {opt.label ?? opt.value}
                        </option>
                      ))}
                    </select>
                  ) : fd?.type === "BOOLEAN" ? (
                    <select
                      className="text-sm border rounded px-1 py-0.5"
                      value={filter.value}
                      onChange={(e) => updateFilter(index, { value: e.target.value })}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input
                      type={inputType}
                      className="text-sm border rounded px-2 py-0.5 w-28"
                      value={filter.value}
                      onChange={(e) => updateFilter(index, { value: e.target.value })}
                      placeholder="Value..."
                    />
                  )}
                  <button
                    onClick={() => removeFilter(index)}
                    className="text-gray-400 hover:text-red-500 ml-1"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              );
            })}
            <button
              onClick={addFilter}
              className="text-sm text-primary hover:text-primary-dark font-medium px-2 py-1"
            >
              + Add filter
            </button>
            {filters.length > 0 && (
              <button
                onClick={() => setFilters([])}
                className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-x-auto overflow-y-hidden min-h-0">
        <div className="flex space-x-4 h-full pb-4 min-w-max">
          {filteredStages.map((stage: any) => (
            <div
              key={stage.id}
              className="flex-shrink-0 w-80 bg-gray-100 rounded-lg flex flex-col"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id, stage.items?.length || 0)}
            >
              <div
                className="p-3 rounded-t-lg"
                style={{ backgroundColor: stage.color || "#E5E7EB" }}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-white flex items-center">
                    <FaGripVertical className="mr-2 cursor-grab opacity-50" />
                    {stage.name}
                    <span className="ml-2 opacity-70 text-xs font-normal">
                      {stage.items?.length || 0}
                    </span>
                  </h3>

                  {stage.computedValues && stage.computedValues.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {stage.computedValues.map((cv: any) => (
                      <span
                        key={cv.id}
                        className="inline-flex items-center gap-1 text-xs bg-black/20 text-white px-1.5 py-0.5 rounded"
                        title={cv.error || undefined}
                      >
                        <HeroIconByName name={cv.icon} className="w-3 h-3 flex-shrink-0 opacity-75" />
                        <span className="opacity-75">{cv.label}:</span>
                        <span className="font-semibold">{cv.error ? "⚠" : formatComputedValue(cv.value, cv.currency)}</span>
                      </span>
                    ))}
                  </div>
                )}

                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleQuickCreateItem(stage.id)}
                      className="text-white opacity-70 hover:opacity-100 p-1"
                      title="Add item"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 p-2 overflow-y-auto space-y-2">
                {stage.items?.map((item: any, index: number) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item, stage.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => {
                      e.stopPropagation();
                      handleDrop(e, stage.id, index);
                    }}
                  >
                    <SalesFunnelItemCard
                      item={item}
                      onClick={() => setSelectedItemId(item.id)}
                      cardLayout={board.cardLayout}
                      fieldDefinitions={board.fieldDefinitions}
                    />
                  </div>
                ))}

                {filters.length > 0 && stage.originalItemsCount > (stage.items?.length || 0) && (
                  <p className="text-xs text-gray-400 italic text-center py-1">
                    {stage.originalItemsCount - (stage.items?.length || 0)} card{(stage.originalItemsCount - (stage.items?.length || 0)) > 1 ? "s" : ""} hidden by filter
                  </p>
                )}

                {creatingItemInStage === stage.id && (
                  <div className="bg-white rounded-md shadow-sm p-3">
                    <input
                      type="text"
                      className="w-full border rounded px-2 py-1 text-sm mb-2"
                      placeholder="Enter item title..."
                      value={newItemTitle}
                      onChange={(e) => setNewItemTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateItem(stage.id);
                        if (e.key === "Escape") setCreatingItemInStage(null);
                      }}
                      autoFocus
                    />
                    <div className="flex space-x-2">
                      <Button
                        sizeVariant="small"
                        onClick={() => handleCreateItem(stage.id)}
                      >
                        Add
                      </Button>
                      <Button
                        sizeVariant="small"
                        variant="outlined"
                        onClick={() => setCreatingItemInStage(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-2 border-t border-gray-200">
                <button
                  onClick={() => handleQuickCreateItem(stage.id)}
                  className="w-full text-left text-gray-500 hover:text-gray-700 text-sm py-1 px-2 rounded hover:bg-gray-200"
                >
                  <FaPlus className="inline mr-2" />
                  Add item
                </button>
              </div>
            </div>
          ))}

          <div
            className="flex-shrink-0 w-80 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:bg-gray-100"
            onClick={() => setShowCreateStageModal(true)}
          >
            <div className="text-center text-gray-400">
              <FaPlus className="mx-auto mb-2 text-2xl" />
              <span>Add Stage</span>
            </div>
          </div>
        </div>
      </div>

      {showCreateStageModal && (
        <Modal
          title="Create New Stage"
          closeFunction={() => setShowCreateStageModal(false)}
          autoHeight
        >
          <div className="space-y-4">
            <FormInput
              label="Stage Name"
              value={newStageName}
              onChange={(e) => setNewStageName(e.target.value)}
              placeholder="e.g., Lead, Qualified, Proposal"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <input
                type="color"
                value={newStageColor}
                onChange={(e) => setNewStageColor(e.target.value)}
                className="w-full h-10 rounded cursor-pointer"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outlined" onClick={() => setShowCreateStageModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateStage}>Create Stage</Button>
            </div>
          </div>
        </Modal>
      )}

      {selectedItemId && (
        <SalesFunnelItemDetailModal
          itemId={selectedItemId}
          boardId={id!}
          fieldDefinitions={board.fieldDefinitions}
          onClose={() => setSelectedItemId(null)}
          onUpdate={refetch}
        />
      )}
    </div>
  );
}
